/**
 * The live «بانتظارك» count (GET /api/v1/workshop/pulse) — ONE shared poll for every badge on the page (side rail,
 * phone menu, bottom tabs, home): every 45 s while the tab is visible, at once on focus / becoming visible, and on
 * refreshPulse() (call it after an action that changes the queue).
 *
 * Only runs for a WORKSHOP login with workshopUiV2; a 403 (switch turned off) stops it until the next sign-in.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';
import { workshopApi, isAbort } from './api';
import type { WsPulse } from './contracts';

const POLL_MS = 45_000;

let current: WsPulse | null = null;
let timer: number | null = null;
let inflight: AbortController | null = null;
let subscribers = 0;
let forbidden = false;
let lastFetch = 0;
/** The login the cached pulse belongs to — a different sign-in starts empty. */
let owner: string | null = null;
const listeners = new Set<(p: WsPulse | null) => void>();

function emit() { listeners.forEach((l) => l(current)); }

async function fetchNow() {
  if (forbidden || typeof document === 'undefined') return;
  if (document.visibilityState === 'hidden') return;
  inflight?.abort();
  const ctrl = new AbortController();
  inflight = ctrl;
  try {
    current = await workshopApi.pulse(ctrl.signal);
    lastFetch = Date.now();
    emit();
  } catch (e) {
    if (isAbort(e)) return;
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 403 || status === 401) { forbidden = true; stop(); }
    // other errors: keep the last value; the next tick retries
  } finally {
    if (inflight === ctrl) inflight = null;
  }
}

const onWake = () => {
  if (document.visibilityState === 'visible' && Date.now() - lastFetch > 5_000) void fetchNow();
};

function start() {
  if (timer != null) return;
  // A page change unmounts and remounts the badges; don't re-ask for a count we got seconds ago.
  if (!current || Date.now() - lastFetch > 5_000) void fetchNow();
  timer = window.setInterval(() => { void fetchNow(); }, POLL_MS);
  window.addEventListener('focus', onWake);
  document.addEventListener('visibilitychange', onWake);
}

function stop() {
  if (timer != null) window.clearInterval(timer);
  timer = null;
  window.removeEventListener('focus', onWake);
  document.removeEventListener('visibilitychange', onWake);
  inflight?.abort();
}

/** Re-read the pulse now (after a confirm, a receipt, a payment …). */
export function refreshPulse(): void {
  if (subscribers > 0) void fetchNow();
}

/** The current pulse, or null (not loaded, not a v2 workshop). */
export function usePulse(): WsPulse | null {
  const { user } = useAuth();
  const enabled = !!user && user.role === UserRole.WORKSHOP && !!user.workshopUiV2;
  const userId = user?.id ?? null;
  const [value, setValue] = useState<WsPulse | null>(enabled ? current : null);
  useEffect(() => {
    if (!enabled) return undefined;
    if (owner !== userId) { owner = userId; current = null; lastFetch = 0; }
    forbidden = false;
    listeners.add(setValue);
    subscribers += 1;
    setValue(current);
    start();
    return () => {
      listeners.delete(setValue);
      subscribers -= 1;
      if (subscribers <= 0) { subscribers = 0; stop(); }
    };
  }, [enabled, userId]);
  return enabled ? value : null;
}
